import _ from 'lodash';

/**
 * Groups a period's line items by the feature they belong to, the way the
 * invoice does.
 *
 * A feature with several cost rows produced one row per cost, each repeating the
 * feature's name ("KI-Bildbearbeitung - Input", "- Output", "- GPT Image 2", and
 * every model that was never used). The number the reader wants first is the one
 * per feature; the per-cost detail is what makes it checkable, so it stays, one
 * level down.
 *
 * Only money is summed onto the heading: the children are counted in tokens,
 * images and seconds, so a combined quantity or unit price would be a figure
 * nobody was charged.
 *
 * The result is a flat list carrying a `groupRole` of parent, child or single,
 * so a table only has to indent.
 */

// A tariff that was never used says nothing. A RECURRING position at 0,00 € is
// the opposite case and stays: it says the feature was supplied, free of charge.
const wasUsed = (item) => item.type !== 'usage' || Number(item.quantity) > 0 || Number(item.total_price) !== 0;

const featureOf = (item) => _.get(item, 'billingFeatureCost.billingFeature');

const childLabel = (item, featureLabel, translate) => {
    const label = item.label || '';
    const prefix = `${featureLabel} - `;

    if (label.indexOf(prefix) === 0) return label.slice(prefix.length);
    if (label !== featureLabel) return label;

    // The feature's recurring position is its base price, whatever the cost row
    // happens to be called.
    if (item.type === 'recurring') return translate('Base price');

    // Only a cost row that belongs to this very feature may name the position: a
    // mismatched pair would otherwise put a foreign product's name on it.
    const cost = item.billingFeatureCost;
    const belongsToFeature =
        _.get(cost, 'billingFeature.id') === String(_.get(item, 'billingFeature.id')) ||
        _.get(cost, 'billingFeature.id') === _.get(item, 'billingFeature.id');

    if (cost && belongsToFeature) return cost.label || cost.cost_type;

    return label;
};

export const groupLineItemsByFeature = (items, translate = (key) => key) => {
    const groups = [];
    const indexOfFeature = {};

    (items || []).filter(wasUsed).forEach((item) => {
        const key = _.get(item, 'billingFeature.id');

        // A feature keeps the place of its FIRST position, so nothing is reordered.
        if (key && indexOfFeature[key] !== undefined) {
            groups[indexOfFeature[key]].items.push(item);
            return;
        }

        groups.push({ items: [item] });

        // Without a feature there is nothing to group by (a manual charge), so
        // each such row stands on its own.
        if (key) indexOfFeature[key] = groups.length - 1;
    });

    const rows = [];

    groups.forEach((group) => {
        const featureLabel = _.get(featureOf(group.items[0]), 'label') || group.items[0].label;
        const labels = group.items.map((item) => childLabel(item, featureLabel, translate));

        // A heading is only worth it when the positions under it can be told
        // apart: children that all repeat the feature's name are a breakdown
        // that breaks nothing down, and naming them from a cost row of another
        // feature would invent a product nobody sold.
        const tellsThemApart =
            group.items.length > 1 &&
            _.uniq(labels).length === labels.length &&
            labels.indexOf(featureLabel) === -1;

        if (!tellsThemApart) {
            group.items.forEach((item) => rows.push({ ...item, groupRole: 'single' }));
            return;
        }

        rows.push({
            id: `group-${_.get(group.items[0], 'billingFeature.id')}`,
            groupRole: 'parent',
            label: featureLabel,
            type: null,
            quantity: null,
            unit_price: null,
            total_price: _.sumBy(group.items, (item) => Number(item.total_price) || 0),
            is_metered: false,
            is_editable: false,
            coverage: null,
        });

        group.items.forEach((item, index) => {
            rows.push({ ...item, groupRole: 'child', label: labels[index] });
        });
    });

    return rows;
};

// Whole days for the "prorated X of Y days" note. Coverage is stored
// fractionally, and a note reading "prorated 12.9032 of 31 days" is noise —
// both period views show the same rounded figure.
export const formatCoverageDays = days => Math.round(parseFloat(days) || 0);

export default { groupLineItemsByFeature, formatCoverageDays };
